import React, { useState, useRef } from 'react';
import { Upload, FileCheck, ChevronDown, Loader2, X, Play, AlertTriangle } from 'lucide-react';
import { RATINGS, DOC_TYPE_LABELS, RATING_CRITERIA, formatRawValue } from '../constants';
import ProvenanceBadge from './ProvenanceBadge';
import RatingCriteriaPopover, { RatingCriteriaButton } from './RatingCriteriaPopover';
import ParseResultBlock from './ParseResultBlock';
import { extractForVariable } from '../api';

/**
 * One variable row in Step 2 (Input Selection).
 *
 * The user can:
 *   - Stage a High-Trust document → click Parse → unlocks doc_verified provenance
 *   - Self-select a rating from the dropdown → keeps the property capped at 89
 *   - Remove either at any time
 *
 * Proxy-only variables (regulatory_friction, market_stress, oversight_credit)
 * render read-only with their baseline rating + source.
 */
export default function VariableRow({ variable, value, onChange, onRecordUpload, onIngestVariables, onExtractedProperty, onExtractedBuildings }) {
    const inputRef = useRef(null);
    const criteriaButtonRef = useRef(null);
    const [stagedFile, setStagedFile] = useState(null);  // File | null
    const [parsing, setParsing] = useState(false);
    const [parseResult, setParseResult] = useState(null);  // ExtractResponse | null
    const [parseError, setParseError] = useState('');
    const [showCriteria, setShowCriteria] = useState(false);

    const current = value || { rating: '', provenance: 'self_selected' };
    const isProxyOnly = !variable.userInput;
    const hasData = !!(current.rating || current.raw || current.source_document);
    const hasCriteria = !!RATING_CRITERIA[variable.key];

    const handleFilePick = (file) => {
        if (!file) return;
        setStagedFile(file);
        setParseResult(null);
        setParseError('');
    };

    const parse = async () => {
        if (!stagedFile) return;
        setParsing(true);
        setParseError('');
        setParseResult(null);
        try {
            const fd = new FormData();
            fd.append('file', stagedFile);
            // Focused per-variable endpoint — passes the variable key so the
            // backend uses the small prompt for just this variable instead of
            // the comprehensive 250-line one.
            fd.append('variable_key', variable.key);
            const res = await extractForVariable(fd);
            if (!res.ok) {
                let detail = 'Extraction failed';
                try { detail = (await res.json()).detail || detail; } catch (_) {}
                throw new Error(detail);
            }
            const data = await res.json();
            setParseResult(data);

            // The endpoint extracts EVERY variable this doc type can prove —
            // not just the requested one. A CNA uploaded to capital_adequacy
            // also fills defect_density_pdd + habitability_density. Lift the
            // full set up so the parent can fan it out across rows.
            const extractedAll = data.extracted_variables || {};
            const otherVars = Object.fromEntries(
                Object.entries(extractedAll).filter(([k]) => k !== variable.key)
            );
            if (Object.keys(otherVars).length && onIngestVariables) {
                onIngestVariables(otherVars, stagedFile.name);
            }

            // The requested variable still updates this row's local state.
            const extracted = extractedAll[variable.key];
            if (extracted) {
                // Merge raw with whatever's already there — preserves proxy
                // data (e.g. baseline AMI) that the doc didn't supply.
                // CRITICAL: filter out null/undefined from extracted raw so
                // explicit nulls from Gemini don't clobber proxy values.
                const incomingRaw = Object.fromEntries(
                    Object.entries(extracted.raw || {}).filter(([, v]) => v != null && v !== '')
                );
                const mergedRaw = {
                    ...(current?.raw || {}),
                    ...incomingRaw,
                };
                onChange({
                    rating: extracted.rating,
                    raw: Object.keys(mergedRaw).length ? mergedRaw : null,
                    provenance: data.is_high_trust ? 'doc_verified' : 'self_selected',
                    source_document: stagedFile.name,
                    // Snapshot the doc-derived rating so we can revert from a later override.
                    _doc_rating: extracted.rating,
                });
            }

            // Property metadata + buildings, if any — both endpoints can return them.
            if (data.property && Object.keys(data.property).some(k => data.property[k]) && onExtractedProperty) {
                onExtractedProperty(data.property);
            }
            if (Array.isArray(data.buildings) && data.buildings.length && onExtractedBuildings) {
                onExtractedBuildings(data.buildings);
            }

            onRecordUpload?.({
                filename: stagedFile.name,
                size_kb: Math.round((stagedFile.size || 0) / 1024),
                document_type: data.document_type,
                inferred_label: data.inferred_label,
                reasoning: data.reasoning,
                is_high_trust: data.is_high_trust,
                source: `variable:${variable.key}`,
                contributed_variables: extracted ? [variable.key] : [],
                contributed_buildings: (data.buildings || []).map(b => b.name),
                uploaded_at: new Date().toISOString(),
            });
        } catch (err) {
            setParseError(err.message || 'Parse failed');
        } finally {
            setParsing(false);
        }
    };

    const clearStaged = () => {
        setStagedFile(null);
        setParseResult(null);
        setParseError('');
    };

    const clearAll = () => {
        onChange(null);
        clearStaged();
    };

    // User picked a self-select rating while a doc was attached — keep the
    // document as evidence, but flip provenance and remember we're now
    // overriding so we can offer a revert.
    const overrideRating = (newRating) => {
        if (!newRating) return;
        onChange({
            ...current,
            rating: newRating,
            provenance: 'self_selected',
        });
    };

    const revertOverride = () => {
        if (!current?._doc_rating) return;
        onChange({
            ...current,
            rating: current._doc_rating,
            provenance: 'doc_verified',
        });
    };

    const hasDocEvidence = current.provenance === 'doc_verified' || current._doc_rating;
    const dropdownPlaceholder = hasDocEvidence ? 'Override rating…' : 'Self-select rating…';

    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-800 dark:text-slate-100">{variable.label}</div>
                        {/* <span className="text-xs text-slate-500 dark:text-slate-400">{variable.range}</span> */}
                        {current.rating && <ProvenanceBadge provenance={current.provenance} />}
                    </div>
                    {variable.docs.length > 0 && (
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Accepts: {variable.docs.join(' · ')}
                        </div>
                    )}
                </div>
                {hasData && !isProxyOnly && (
                    <button onClick={clearAll}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors"
                            title="Remove value">
                        <X size={14} /> Remove
                    </button>
                )}
            </div>

            {isProxyOnly ? (
                <div className="space-y-1">
                    <div className="flex items-center justify-between gap-2 text-sm flex-wrap">
                        <span className="text-slate-600 dark:text-slate-300">
                            System-driven — <span className="font-medium text-slate-800 dark:text-slate-100">{current.rating || 'Moderate'}</span>
                        </span>
                        {current.source && (
                            <span className="text-[11px] text-slate-500 dark:text-slate-400 tracking-tight">
                                source: {current.source}
                            </span>
                        )}
                    </div>
                    {/* Surface the underlying figures that drove the rating */}
                    {current.raw && formatRawValue(variable.key, current.raw) && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                            {formatRawValue(variable.key, current.raw)}
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <div className="flex gap-3 items-stretch w-full min-w-0">
                        {/* Stage / Parse zone — min-w-0 lets flex children honor truncation */}
                        <div className="flex-1 min-w-0">
                            {!stagedFile ? (
                                <button type="button" onClick={() => inputRef.current?.click()}
                                        title={current.source_document || 'Stage High-Trust Document'}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border-2 border-dashed border-green-400 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors overflow-hidden">
                                    <Upload size={16} className="flex-shrink-0" />
                                    {current.source_document ? <FileCheck size={16} className="flex-shrink-0" /> : null}
                                    <span className="text-sm font-medium truncate min-w-0">
                                        {current.source_document || 'Stage High-Trust Document'}
                                    </span>
                                </button>
                            ) : (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 overflow-hidden">
                                    <FileCheck size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                    <span title={stagedFile.name} className="text-sm text-slate-800 dark:text-slate-100 truncate flex-1 min-w-0">{stagedFile.name}</span>
                                    {!parsing && !parseResult && (
                                        <button onClick={parse}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-950 rounded-md transition-colors flex-shrink-0">
                                            <Play size={11} /> Parse
                                        </button>
                                    )}
                                    {parsing && (
                                        <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs font-medium">
                                            <Loader2 size={12} className="animate-spin" /> Parsing…
                                        </span>
                                    )}
                                    {/* Replace / Try another — always available unless mid-parse */}
                                    {!parsing && parseResult && !parseResult.is_high_trust && (
                                        <button type="button" onClick={() => inputRef.current?.click()}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-950 rounded-md transition-colors flex-shrink-0">
                                            <Upload size={11} /> Try another
                                        </button>
                                    )}
                                    {!parsing && (
                                        <button onClick={clearStaged} className="p-1 text-slate-400 hover:text-red-600 transition-colors flex-shrink-0" title="Remove file">
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            )}
                            <input ref={inputRef} type="file" accept=".pdf,.docx,.xlsx,.xls,image/*" className="hidden"
                                   onChange={(e) => handleFilePick(e.target.files?.[0])} />
                        </div>

                        {/* Self-select / Override dropdown + criteria popover trigger */}
                        <div className="relative w-48 flex-shrink-0">
                            <select
                                value={current.provenance === 'self_selected' ? current.rating || '' : ''}
                                onChange={(e) => hasDocEvidence
                                    ? overrideRating(e.target.value)
                                    : onChange({ rating: e.target.value, provenance: 'self_selected' })}
                                className="appearance-none w-full px-3 py-2 pr-8 rounded-md border border-slate-300 dark:border-slate-700
                                           bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-slate-100
                                           focus:ring-2 focus:ring-blue-500 focus:outline-none">
                                <option value="">{dropdownPlaceholder}</option>
                                {RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <ChevronDown size={16} className="absolute right-2 top-2.5 text-slate-400 pointer-events-none" />

                            {hasCriteria && (
                                <RatingCriteriaPopover
                                    variableKey={variable.key}
                                    label={variable.label}
                                    anchorRef={criteriaButtonRef}
                                    open={showCriteria}
                                    onClose={() => setShowCriteria(false)}
                                />
                            )}
                        </div>
                    </div>

                    {hasCriteria && !hasData && (
                        <div className="mt-2">
                            <RatingCriteriaButton buttonRef={criteriaButtonRef}
                                                  onClick={() => setShowCriteria(v => !v)} />
                        </div>
                    )}

                    {/* Post-parse evidence block — what was extracted, what rating, why */}
                    <ParseResultBlock variable={variable} value={current}
                                      onRevertOverride={current._doc_rating ? revertOverride : undefined} />

                    {/* Parse result feedback */}
                    {parseResult && parseResult.is_high_trust && parseResult.extracted_variables?.[variable.key] && (
                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-200 text-[11px] font-medium">
                            Detected: {parseResult.inferred_label || DOC_TYPE_LABELS[parseResult.document_type] || parseResult.document_type}
                        </div>
                    )}
                    {parseResult && !parseResult.is_high_trust && (
                        <div className="mt-2 p-2 rounded-md bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900">
                            <div className="flex items-start gap-1.5 text-xs text-orange-800 dark:text-orange-200">
                                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-medium">This looks like a {parseResult.inferred_label || 'unrecognized document'} — can't use it for {variable.label}.</div>
                                    {parseResult.reasoning && <div className="mt-1 text-orange-700 dark:text-orange-300 leading-relaxed">{parseResult.reasoning}</div>}
                                </div>
                            </div>
                        </div>
                    )}
                    {parseResult && parseResult.is_high_trust && !parseResult.extracted_variables?.[variable.key] && (
                        <div className="mt-2 p-2 rounded-md bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900">
                            <div className="flex items-start gap-1.5 text-xs text-orange-800 dark:text-orange-200">
                                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                                <div>
                                    Detected as {parseResult.inferred_label || DOC_TYPE_LABELS[parseResult.document_type]}, but this report doesn't prove {variable.label}. Try a different document type.
                                </div>
                            </div>
                        </div>
                    )}
                    {parseError && (
                        <div className="mt-2 text-xs text-red-700 dark:text-red-300">{parseError}</div>
                    )}
                </>
            )}
        </div>
    );
}
